import React from "react";
import { Box, Typography, IconButton, Avatar, Button } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import dayjs from "dayjs";

const ANNOUNCEMENT_BG = "#fff8e1";

const initialsOf = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.charAt(0) || ""}${parts[1]?.charAt(0) || ""}`.toUpperCase();
};

const ChatMessage = ({ message, onDelete, onRetry, onDiscard }) => {
  const when = dayjs(message.createdAt).format("MMM D, h:mm A");

  if (message.deleted) {
    return (
      <Box sx={{ py: 0.75 }}>
        <Typography sx={{ fontSize: 12, fontStyle: "italic", color: "text.disabled" }}>
          {message.deletedReason === "author"
            ? "This message was deleted."
            : "This message was removed by the organizer."}
        </Typography>
      </Box>
    );
  }

  if (message.authorType === "organizer") {
    return (
      <Box
        sx={{
          my: 0.75,
          p: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: "warning.light",
          bgcolor: ANNOUNCEMENT_BG,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
          <CampaignIcon sx={{ fontSize: 18, color: "warning.main" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            {message.displayName || "Organizer"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary", ml: "auto" }}>
            {when}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {message.text}
        </Typography>
      </Box>
    );
  }

  const mine = message.isMine;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        my: 0.75,
        flexDirection: mine ? "row-reverse" : "row",
        opacity: message.pending ? 0.6 : 1,
      }}
    >
      {!mine && (
        <Avatar sx={{ width: 30, height: 30, fontSize: 12 }}>
          {initialsOf(message.authorName)}
        </Avatar>
      )}
      <Box sx={{ maxWidth: "78%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            justifyContent: mine ? "flex-end" : "flex-start",
          }}
        >
          {!mine && (
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {message.authorName || "Player"}
            </Typography>
          )}
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
            {message.failed ? "Not sent" : when}
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 0.25,
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: mine ? "primary.main" : "grey.100",
            color: mine ? "primary.contrastText" : "text.primary",
          }}
        >
          <Typography sx={{ fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {message.text}
          </Typography>
        </Box>
        {message.failed && (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              justifyContent: mine ? "flex-end" : "flex-start",
            }}
          >
            <Button size="small" onClick={() => onRetry(message)}>
              Retry
            </Button>
            <Button size="small" color="inherit" onClick={() => onDiscard(message)}>
              Discard
            </Button>
          </Box>
        )}
        {message.canDelete && !message.pending && !message.failed && (
          <Box sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
            <IconButton
              size="small"
              aria-label="Delete message"
              onClick={() => onDelete(message)}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
